// Cross-platform compatibility file for case-sensitive filesystems (Linux/Windows)


import "./index.css";

// Intercept invite code from URL before routing strips query params.
// Email invite links include ?invite=CODE — stash for onboarding to read.
(function stashInviteParam() {
  const params = new URLSearchParams(window.location.search);
  const invite = params.get('invite');
  if (invite) {
    localStorage.setItem('moby-pending-invite-code', invite);
    params.delete('invite');
    const remaining = params.toString();
    const clean = window.location.pathname + (remaining ? `?${remaining}` : '') + window.location.hash;
    window.history.replaceState({}, '', clean);
  }
})();

import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Toaster } from "sonner";
import { Route, Switch } from "wouter";
import { ChatListPanel, type SelectedEmail } from "./components/chat/chat-list-panel";
import { ChatPanel } from "./components/chat/chat-panel";
import { EmailViewPanel } from "./components/chat/email-view-panel";
import { AVStreamPanel } from "./components/chat/av-stream-panel";
import { CalendarPanel } from "./components/chat/calendar-panel";
import { CalendarDayDialog } from "./components/chat/calendar-day-dialog";
import { MobyChatPageLayout } from "./components/mobychat-page-layout";
import { OnboardingLoginPage } from "./components/onboarding-login-page";
import { OnboardingPage } from "./components/onboarding-page";
import { MobyProvider } from "./components/provider";
import { LayoutResizeHandle } from "./components/layout-resize-handle";
import { SelfHealingSuspense } from "./components/self-healing-suspense";

function MainContentPanel() {
  const [selectedEmail, setSelectedEmail] = useState<SelectedEmail | null>(null);
  const [composeMode, setComposeMode] = useState(false);
  const [showStreamPanel, setShowStreamPanel] = useState(false);
  const [showCalendarPanel, setShowCalendarPanel] = useState(false);
  const prevLocationRef = useRef(location.toString());

  // Clear overlay panels when the user navigates to a different chat route
  useEffect(() => {
    const checkNavigation = () => {
      const current = location.toString();
      if (current !== prevLocationRef.current) {
        prevLocationRef.current = current;
        // User navigated — dismiss overlay panels so ChatPanel shows
        setShowStreamPanel(false);
        setShowCalendarPanel(false);
        setSelectedEmail(null);
        setComposeMode(false);
      }
    };
    // popstate fires on back/forward, but Link components just push state
    // Use a short interval to catch all navigation types
    const interval = setInterval(checkNavigation, 100);
    window.addEventListener('popstate', checkNavigation);
    return () => {
      clearInterval(interval);
      window.removeEventListener('popstate', checkNavigation);
    };
  }, []);

  useEffect(() => {
    const handleEmailSelected = (event: CustomEvent<SelectedEmail>) => {
      setSelectedEmail(event.detail);
      setComposeMode(false);
      setShowStreamPanel(false);
      setShowCalendarPanel(false);
    };

    const handleComposeNew = () => {
      setComposeMode(true);
      setSelectedEmail(null);
      setShowStreamPanel(false);
      setShowCalendarPanel(false);
    };

    const handleEmailPanelClose = () => {
      setSelectedEmail(null);
      setComposeMode(false);
    };

    const handleStreamSelected = () => {
      setShowStreamPanel(true);
      setSelectedEmail(null);
      setComposeMode(false);
      setShowCalendarPanel(false);
    };

    const handleStreamClosed = () => {
      setShowStreamPanel(false);
    };

    const handleCalendarOpen = () => {
      setShowCalendarPanel(true);
      setSelectedEmail(null);
      setComposeMode(false);
      setShowStreamPanel(false);
    };

    const handleCalendarClose = () => {
      setShowCalendarPanel(false);
    };

    window.addEventListener("email-selected", handleEmailSelected as EventListener);
    window.addEventListener("email-compose-new", handleComposeNew);
    window.addEventListener("email-panel-close", handleEmailPanelClose);
    window.addEventListener("av-stream-panel-open", handleStreamSelected);
    window.addEventListener("av-stream-panel-close", handleStreamClosed);
    window.addEventListener("calendar-panel-open", handleCalendarOpen);
    window.addEventListener("calendar-panel-close", handleCalendarClose);
    return () => {
      window.removeEventListener("email-selected", handleEmailSelected as EventListener);
      window.removeEventListener("email-compose-new", handleComposeNew);
      window.removeEventListener("email-panel-close", handleEmailPanelClose);
      window.removeEventListener("av-stream-panel-open", handleStreamSelected);
      window.removeEventListener("av-stream-panel-close", handleStreamClosed);
      window.removeEventListener("calendar-panel-open", handleCalendarOpen);
      window.removeEventListener("calendar-panel-close", handleCalendarClose);
    };
  }, []);

  if (showCalendarPanel) {
    return <CalendarPanel />;
  }

  if (showStreamPanel) {
    return <AVStreamPanel />;
  }

  if (selectedEmail) {
    return (
      <EmailViewPanel
        selectedEmail={selectedEmail}
        onBack={() => setSelectedEmail(null)}
      />
    );
  }

  if (composeMode) {
    return (
      <EmailViewPanel
        selectedEmail={null as unknown as SelectedEmail}
        onBack={() => setComposeMode(false)}
        composeOnly
      />
    );
  }

  return <ChatPanel key={location.toString()} />;
}

// CRITICAL FIX: Stable component reference for /chats route.
// Previously this was an inline arrow function inside App: component={() => (...)}
// Every App re-render (triggered by top-level Suspense resolution) created a NEW
// function reference, which React treated as a different component type — causing
// the ENTIRE MobyChatPageLayout subtree to unmount/remount. This cascaded every
// useSuspenseLiveQuery cache miss into a full-page blank flash (the "login flash" bug).
function ChatsPage() {
  return (
    <MobyChatPageLayout>
      <SelfHealingSuspense
        name="chat-list"
        watchdogMs={5000}
        maxRemounts={2}
        unboundedRetry
        fallback={
          <div className="flex w-full flex-col sm:mr-6 sm:w-96 sm:shrink-0 animate-pulse">
            <div className="bg-surface-container-highest/30 m-4 h-8 rounded-full" />
            <div className="bg-surface-container-highest/20 mx-4 mb-2 h-12 rounded-lg" />
            <div className="bg-surface-container-highest/20 mx-4 mb-2 h-12 rounded-lg" />
            <div className="bg-surface-container-highest/20 mx-4 mb-2 h-12 rounded-lg" />
          </div>
        }
      >
        <ChatListPanel />
      </SelfHealingSuspense>
      <LayoutResizeHandle />
      <SelfHealingSuspense
        name="main-content"
        watchdogMs={5000}
        maxRemounts={2}
        unboundedRetry
        fallback={
          <div className="bg-surface rounded-t-large relative flex flex-1 flex-col items-center justify-center overflow-hidden sm:mr-6">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        }
      >
        <MainContentPanel />
      </SelfHealingSuspense>
      <CalendarDayDialog />
    </MobyChatPageLayout>
  );
}

export function App() {
  return (
    <MobyProvider>
      <Switch>
        <Route path="/" component={OnboardingLoginPage} />

        <Route path="/onboarding" component={OnboardingPage} />
        <Route path="/chats/:chatAddress?" component={ChatsPage} />
      </Switch>
      {createPortal(<Toaster />, document.body)}
    </MobyProvider>
  );
}


// Default export for easier imports
export default App;
